import { create } from 'zustand';
import {
  api,
  refreshSession,
  setAccessToken,
  setSessionLostHandler,
  ApiError,
} from '../lib/api-client';
import type { AuthTokens, AuthUser, OtpRequestResult, VerifyOtpResult } from '../lib/api-types';

/**
 * Sessão do cliente.
 *
 * ---
 *
 * TRÊS ESTADOS, E NÃO DOIS
 *
 * `checking` existe porque, no primeiro instante, o app AINDA NÃO SABE se há
 * sessão — o refresh token está num cookie que só o servidor lê. Sem esse
 * estado, a tela protegida piscaria o login por uma fração de segundo em toda
 * abertura do app, e quem já estava logado veria a tela de entrar antes de ser
 * jogado para dentro.
 */
export type AuthStatus = 'checking' | 'authenticated' | 'anonymous';

interface AuthState {
  readonly status: AuthStatus;
  readonly user: AuthUser | null;
  /** Telefone do último pedido de código, para a tela de confirmação. */
  readonly pendingPhone: string | null;
  /** Devolvido pelo servidor quando o telefone ainda não tem cadastro. */
  readonly signupToken: string | null;

  bootstrap: () => Promise<void>;
  requestOtp: (phone: string) => Promise<OtpRequestResult>;
  verifyOtp: (code: string) => Promise<'authenticated' | 'registration_required'>;
  register: (input: { name: string; email?: string; birthDate?: string }) => Promise<void>;
  logout: () => Promise<void>;
  clearPending: () => void;
}

function applyTokens(tokens: AuthTokens): void {
  setAccessToken(tokens.accessToken);
}

export const useAuthStore = create<AuthState>((set, get) => ({
  status: 'checking',
  user: null,
  pendingPhone: null,
  signupToken: null,

  /**
   * Tenta ressuscitar a sessão a partir do cookie de refresh.
   *
   * Roda na abertura. Falhar aqui é o caso NORMAL de quem nunca entrou — por
   * isso nenhum erro é propagado: o resultado é simplesmente "anônimo".
   *
   * Passa por `refreshSession()`, que é de voo único. Chamar `/auth/refresh`
   * direto daqui parecia inofensivo e não era: o `StrictMode` executa o efeito
   * de abertura duas vezes, as duas chamadas gastavam tokens diferentes da
   * mesma família, e o servidor — corretamente — tratava a segunda como token
   * roubado e derrubava a sessão inteira. O sintoma era entrar, recarregar e
   * cair no login.
   */
  async bootstrap() {
    const sessao = await refreshSession();

    if (!sessao) {
      setAccessToken(null);
      set({ status: 'anonymous', user: null });
      return;
    }

    set({ status: 'authenticated', user: sessao.user });
  },

  async requestOtp(phone) {
    const result = await api.post<OtpRequestResult>('/auth/otp/request', { phone });
    set({ pendingPhone: phone, signupToken: null });
    return result;
  },

  async verifyOtp(code) {
    const phone = get().pendingPhone;

    if (!phone) throw new ApiError(400, 'NO_PENDING_PHONE', 'Peça um novo código para continuar.');

    const result = await api.post<VerifyOtpResult>('/auth/otp/verify', { phone, code });

    if ('registrationRequired' in result) {
      set({ signupToken: result.signupToken });
      return 'registration_required';
    }

    applyTokens(result);
    set({ status: 'authenticated', user: result.user, signupToken: null });
    return 'authenticated';
  },

  async register(input) {
    const signupToken = get().signupToken;

    if (!signupToken) {
      throw new ApiError(400, 'NO_SIGNUP_TOKEN', 'Confirme seu telefone novamente para continuar.');
    }

    // O telefone NÃO vai no corpo: ele vem de dentro do signupToken. Quem
    // verificou um número não pode cadastrar outro.
    const tokens = await api.post<AuthTokens>('/auth/register', { signupToken, ...input });

    applyTokens(tokens);
    set({ status: 'authenticated', user: tokens.user, signupToken: null, pendingPhone: null });
  },

  async logout() {
    try {
      await api.post('/auth/logout');
    } catch {
      // O servidor pode já ter revogado a sessão. Sair localmente assim mesmo é
      // o comportamento que o cliente pediu ao tocar em "sair".
    }

    setAccessToken(null);
    set({ status: 'anonymous', user: null, pendingPhone: null, signupToken: null });
  },

  clearPending() {
    set({ pendingPhone: null, signupToken: null });
  },
}));

/**
 * Quando o refresh falha no meio do uso, a sessão acabou de verdade.
 *
 * Registrado uma vez, fora do componente: se cada tela cuidasse disso, a que
 * esquecesse deixaria o app mostrando dados de alguém que não está mais logado.
 */
setSessionLostHandler(() => {
  useAuthStore.setState({
    status: 'anonymous',
    user: null,
    pendingPhone: null,
    signupToken: null,
  });
});
